<template>
  <b-container fluid class="py-5">
    <!-- Notification Banner - Hidden if localStorage has icNumber -->
    <b-row v-if="!hasIcNumberInLocalStorage" class="mb-5">
      <b-col>
        <b-alert show class="gradient-alert shadow-md rounded-pill py-3 px-4 d-flex align-items-center pulse-animation">
          <i class="fas fa-info-circle mr-3" style="font-size: 1.5rem; color: #fff;"></i>
          <span class="font-weight-bold text-white" style="font-size: 1rem;">
            MAKLUMAN: Maklumat Rekod Peribadi yang dipaparkan adalah daripada Sistem HRMIS
          </span>
        </b-alert>
      </b-col>
    </b-row>

    <!-- Profile Card -->
    <b-card no-body class="profile-card shadow-lg rounded-xl overflow-hidden">
      <b-card-header class="bg-gradient-teal animated-header py-5 px-5">
        <b-row align-v="center">
          <b-col cols="auto">
            <div class="avatar-placeholder rounded-circle bg-white text-teal d-flex align-items-center justify-content-center tilt-animation">
              <i class="fas fa-user fa-2x"></i>
            </div>
          </b-col>
          <b-col>
            <h2 class="mb-0 font-weight-bold text-uppercase text-white">Profil Pengguna</h2>
            <p class="mb-0 text-white-75" style="font-size: 0.95rem;">
              {{ hasIcNumberInLocalStorage ? 'Maklumat Peribadi' : 'Maklumat Peribadi dan Perjawatan' }}
            </p>
          </b-col>
        </b-row>
      </b-card-header>
      <b-card-body class="p-5 glassmorphism-bg">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-4">
          <b-spinner variant="primary" label="Loading..."></b-spinner>
          <p class="mt-2 text-muted">Memuat maklumat profil...</p>
        </div>

        <!-- Profile Content -->
        <div v-else>
          <!-- For localStorage users with icNumber - Show only 4 fields -->
          <div v-if="hasIcNumberInLocalStorage">
            <h3 class="font-weight-bold text-dark text-uppercase mb-4 fade-in">Rekod Peribadi</h3>
            <b-row>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.1s;">
                <b-form-group label="Nama" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.name || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.2s;">
                <b-form-group label="No. Kad Pengenalan" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.icno || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.3s;">
                <b-form-group label="Alamat Emel" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.email || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.4s;">
                <b-form-group label="Agensi" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.agency || '-' }}</p>
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- For regular users (without icNumber) - Show all fields -->
          <div v-else>
            <!-- Rekod Peribadi -->
            <h3 class="font-weight-bold text-dark text-uppercase mb-4 fade-in">Rekod Peribadi</h3>
            <b-row>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.1s;">
                <b-form-group label="Nama" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.name || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.2s;">
                <b-form-group label="No. Kad Pengenalan" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.icno || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.3s;">
                <b-form-group label="Alamat Emel" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.email || '-' }}</p>
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Data Perjawatan -->
            <h3 class="font-weight-bold text-dark text-uppercase mb-4 mt-5 fade-in" style="animation-delay: 0.4s;">Data Perjawatan</h3>
            <b-row>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.5s;">
                <b-form-group label="Organisasi" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.kementerian || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.6s;">
                <b-form-group label="Jabatan / Agensi" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.agency || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.7s;">
                <b-form-group label="Unit Organisasi" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">{{ user.organization_unit || '-' }}</p>
                </b-form-group>
              </b-col>
              <b-col lg="6" md="12" class="mb-4 fade-in" style="animation-delay: 0.8s;">
                <b-form-group label="Jawatan" label-class="font-weight-bold text-muted">
                  <p class="form-control-static text-dark hover-highlight">
                    {{ user.jawatan ? user.jawatan.toUpperCase() : '-' }} / {{ user.gred ? user.gred.toUpperCase() : '-' }}
                  </p>
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Debug Information (remove in production) -->
    <!-- <div v-if="showDebug" class="mt-3">
      <b-card>
        <b-card-header>Debug Information</b-card-header>
        <b-card-body>
          <pre>{{ debugInfo }}</pre>
        </b-card-body>
      </b-card>
    </div> -->
  </b-container>
</template>

<script>
import axios from 'axios';
import { baseURL } from '../../../services/baseURL';

export default {
  name: 'UserProfile',
  data() {
    return {
      user: {},
      loading: true,
      showDebug: false, // Set to true for debugging
      debugInfo: {},
      hasIcNumberInLocalStorage: false
    };
  },
  methods: {
    /**
     * Check if localStorage contains icNumber
     */
    checkLocalStorageIcNumber() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const parsedData = JSON.parse(userData);
          this.hasIcNumberInLocalStorage = !!(parsedData && parsedData.icNumber);
        } else {
          this.hasIcNumberInLocalStorage = false;
        }
      } catch (error) {
        console.error('Error checking localStorage icNumber:', error);
        this.hasIcNumberInLocalStorage = false;
      }
    },

    /**
     * Get localStorage data safely
     */
    getLocalStorageData() {
      try {
        const userData = localStorage.getItem('user');
        return userData ? JSON.parse(userData) : null;
      } catch (error) {
        console.error('Error parsing localStorage data:', error);
        return null;
      }
    },

    /**
     * Safe access to nested object properties
     */
    getNestedValue(obj, path, defaultValue = '') {
      try {
        return path.split('.').reduce((current, key) => {
          return current && current[key] !== undefined ? current[key] : defaultValue;
        }, obj);
      } catch (error) {
        return defaultValue;
      }
    },

    /**
     * Fetch profile data from API
     */
    async getProfile() {
      this.loading = true;

      try {
        // Get data from localStorage
        const localStorageData = this.getLocalStorageData();

        // Prepare request data
        let requestData = {};

        // Check if localStorage has the expected format
        if (localStorageData && localStorageData.icNumber) {
          // Use localStorage format
          requestData = {
            localStorage: localStorageData,
            // Also send in direct format for compatibility
            icNumber: localStorageData.icNumber,
            userType: localStorageData.userType || localStorageData.peranan
          };

          // console.log('Using localStorage data:', localStorageData);

          this.debugInfo = {
            source: 'localStorage',
            data: localStorageData,
            requestData: requestData
          };
        } else {
          // Fallback to store/traditional method
          const user = JSON.parse(localStorage.getItem('user')) || {};

          // Safe access to store state
          const storeState = this.getStoreState();

          const icno = user.icno || this.getNestedValue(storeState, 'icno', '');
          const peranan = this.getNestedValue(storeState, 'peranan', '');
          const nama = this.getNestedValue(storeState, 'nama', '');
          const user_type = user.user_type || '';

          requestData = {
            table: {
              request: {
                NoKP: icno,
                peranan: peranan,
                nama: nama,
                user_type: user_type,
              },
            },
          };

          // console.log('Using traditional format:', requestData);

          this.debugInfo = {
            source: 'traditional',
            store: storeState,
            user: user,
            requestData: requestData
          };
        }

        // Make API call
        const response = await axios.post(
          `${baseURL}/api_profile.php`,
          requestData,
          { headers: { 'Content-Type': 'application/json' } }
        );

        if (response.data.rcode === '0') {
          const personalInfo = response.data.data;
          this.user = {
            name: personalInfo.name,
            icno: personalInfo.icno,
            email: personalInfo.email,
            agency: personalInfo.agency,
            kementerian: personalInfo.kementerian,
            organization_unit: personalInfo.organization_unit,
            jawatan: personalInfo.jawatan,
            gred: personalInfo.gred,
          };

          // Update store if available
          if (this.hasStore()) {
            this.updateNama(personalInfo.name);
            this.updatePeranan(personalInfo.status_entitle);
          }

          // Update localStorage if using new format
          if (localStorageData && localStorageData.icNumber) {
            this.updateLocalStorage(personalInfo);
          }

          // console.log('Profile loaded successfully:', this.user);
        } else {
          console.error('Error fetching profile:', response.data.message);
          this.showToast('Gagal memuat profil pengguna', 'Ralat', 'danger');
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        this.showToast('Ralat komunikasi dengan pelayan', 'Ralat', 'danger');
      } finally {
        this.loading = false;
      }
    },

    /**
     * Get store state safely
     */
    getStoreState() {
      try {
        if (this.$store && this.$store.state && this.$store.state.pengguna) {
          return this.$store.state.pengguna;
        }
        return {};
      } catch (error) {
        console.error('Error accessing store:', error);
        return {};
      }
    },

    /**
     * Check if store is available
     */
    hasStore() {
      return this.$store && this.$store.commit;
    },

    /**
     * Update localStorage with fresh data from API
     */
    updateLocalStorage(personalInfo) {
      try {
        const currentData = this.getLocalStorageData();
        if (currentData) {
          const updatedData = Object.assign({}, currentData, {
            nama: personalInfo.name,
            emel: personalInfo.email,
            // Add any other fields you want to update
          });

          localStorage.setItem('user', JSON.stringify(updatedData));
          // console.log('localStorage updated with fresh data');
        }
      } catch (error) {
        console.error('Error updating localStorage:', error);
      }
    },

    /**
     * Update nama in store
     */
    updateNama(nama) {
      try {
        if (this.hasStore()) {
          this.$store.commit('setNama', nama);
        }
      } catch (error) {
        console.error('Error updating nama in store:', error);
      }
    },

    /**
     * Update peranan in store
     */
    updatePeranan(status_entitle) {
      try {
        if (this.hasStore()) {
          this.$store.commit('setPeranan', status_entitle);
        }
      } catch (error) {
        console.error('Error updating peranan in store:', error);
      }
    },

    /**
     * Check if user data is valid
     */
    isValidUserData(data) {
      return data && (data.icNumber || data.icno) && data.valid !== false;
    },

    /**
     * Show toast notification
     */
    showToast(message, title, variant) {
      try {
        if (this.$bvToast) {
          this.$bvToast.toast(message, {
            title: title,
            variant: variant,
            solid: true
          });
        } else {
          // Fallback to console if toast is not available
          // console.log(`${title}: ${message}`);
        }
      } catch (error) {
        console.error('Error showing toast:', error);
      }
    }
  },

  created() {
    // Check localStorage icNumber first
    this.checkLocalStorageIcNumber();
    // Then get profile
    this.getProfile();
  },

  mounted() {
    // Check if we're in development mode to show debug info
    try {
      this.showDebug = process.env.NODE_ENV === 'development';
    } catch (error) {
      this.showDebug = false;
    }
  }
};
</script>

<style scoped>
.profile-card {
  background: #fff;
  border: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.profile-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1) !important;
}

.card-header {
  background: linear-gradient(135deg, #4682b4, #2f4f4f);
  border-bottom: none;
  padding: 2rem;
  position: relative;
  overflow: hidden;
}

.animated-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 200%;
  height: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1));
  animation: shimmer 5s infinite linear;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.card-body {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 0 0 1rem 1rem;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.form-control-static {
  font-size: 1rem;
  color: #2d3748;
  margin-bottom: 0;
  line-height: 1.7;
  transition: color 0.3s ease;
}

.text-dark {
  color: #2d3748 !important;
}

.text-muted {
  color: #718096 !important;
}

.text-teal {
  color: #2f4f4f !important;
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75) !important;
}

h2 {
  font-size: 1.9rem;
  letter-spacing: 0.6px;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

h3 {
  font-size: 1.35rem;
  letter-spacing: 0.4px;
}

label {
  font-size: 0.95rem;
  letter-spacing: 0.2px;
}

.avatar-placeholder {
  width: 68px;
  height: 68px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.tilt-animation {
  transform-style: preserve-3d;
  transition: transform 0.3s ease;
}

.tilt-animation:hover {
  transform: perspective(1000px) rotateX(4deg) rotateY(4deg);
}

.gradient-alert {
  background: linear-gradient(135deg, #87ceeb, #b0e0e6);
  border: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.gradient-alert:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.pulse-animation {
  animation: pulse 2.5s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.015); }
}

.bg-gradient-teal {
  background: linear-gradient(135deg, #4682b4, #2f4f4f);
}

.glassmorphism-bg {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
}

.hover-highlight:hover {
  color: #4682b4 !important;
  transition: color 0.3s ease;
}

.fade-in {
  opacity: 0;
  animation: fadeIn 0.7s ease forwards;
}

@keyframes fadeIn {
  to { opacity: 1; }
}

/* Loading spinner styling */
.text-center .spinner-border {
  width: 3rem;
  height: 3rem;
}
</style>
